import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Select, Spin, message } from 'antd';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, BarChart, Bar, ResponsiveContainer } from 'recharts';
import { statsAPI } from '../services/api';

const { Option } = Select;

const Statistics = () => {
  const [overviewStats, setOverviewStats] = useState(null);
  const [dailyStats, setDailyStats] = useState([]);
  const [realtimeStats, setRealtimeStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  useEffect(() => {
    fetchAllStats();
  }, [days]);

  const fetchAllStats = async () => {
    setLoading(true);
    try {
      const [overviewRes, dailyRes, realtimeRes] = await Promise.all([
        statsAPI.getOverview(days),
        statsAPI.getDaily(days),
        statsAPI.getRealtime()
      ]);

      setOverviewStats(overviewRes.data);
      setDailyStats(dailyRes.data);
      setRealtimeStats(realtimeRes.data);
    } catch (error) {
      message.error('获取统计数据失败');
    } finally {
      setLoading(false);
    }
  };

  // 准备图表数据
  const chartData = dailyStats.map(item => ({
    date: item.date,
    requests: item.requests,
    delivered: item.delivered,
    rate: item.rate,
  }));

  // 准备小时统计数据
  const hourlyData = overviewStats?.requests_by_hour?.map(item => ({
    hour: item.hour.substring(11, 16), // 只取时分
    requests: item.requests,
    delivered: item.delivered,
  })) || [];

  if (loading) {
    return <Spin size="large" />;
  }

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <Select value={days} onChange={setDays} style={{ width: 120 }}>
          <Option value={7}>最近7天</Option>
          <Option value={30}>最近30天</Option>
          <Option value={90}>最近90天</Option>
        </Select>
      </div>

      {/* 实时统计 */}
      {realtimeStats && (
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={6}>
            <Card>
              <div>最近1小时请求数</div>
              <div style={{ fontSize: 24, fontWeight: 'bold', color: '#1890ff' }}>
                {realtimeStats.requests_last_hour}
              </div>
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <div>最近1小时分发数</div>
              <div style={{ fontSize: 24, fontWeight: 'bold', color: '#52c41a' }}>
                {realtimeStats.delivered_last_hour}
              </div>
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <div>5分钟活跃用户</div>
              <div style={{ fontSize: 24, fontWeight: 'bold', color: '#722ed1' }}>
                {realtimeStats.active_users_5min}
              </div>
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <div>平均响应时间(ms)</div>
              <div style={{ fontSize: 24, fontWeight: 'bold', color: '#fa8c16' }}>
                {realtimeStats.avg_response_time_ms?.toFixed(2)}
              </div>
            </Card>
          </Col>
        </Row>
      )}

      {/* 日统计趋势图 */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={24}>
          <Card title="每日请求趋势">
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="requests" stroke="#1890ff" name="请求数" />
                <Line type="monotone" dataKey="delivered" stroke="#52c41a" name="分发数" />
              </LineChart>
            </ResponsiveContainer>
          </Card>
        </Col>
      </Row>

      {/* 分发率趋势 */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={24}>
          <Card title="分发率趋势">
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis domain={[0, 100]} />
                <Tooltip formatter={(value) => [`${value}%`, '分发率']} />
                <Line type="monotone" dataKey="rate" stroke="#fa8c16" name="分发率 (%)" />
              </LineChart>
            </ResponsiveContainer>
          </Card>
        </Col>
      </Row>

      {/* 小时统计 */}
      <Row gutter={16}>
        <Col span={24}>
          <Card title="最近24小时请求分布">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={hourlyData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="hour" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="requests" fill="#1890ff" name="请求数" />
                <Bar dataKey="delivered" fill="#52c41a" name="分发数" />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Statistics;
